// shell/app.config.ts
import {
  ApplicationConfig,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './navigation/app.routes';

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AuthService } from '../application/authentication/application/services/auth.service';
import { AUTH_PROVIDER_PORT } from '../application/authentication/application/ports/auth-provider.port';
import { AuthProviderService } from '../application/authentication/provider/services/auth-provider.service';
import { errorInterceptor } from '../application/common/interceptors/error.interceptor';
import { tokenInterceptor } from '../application/common/interceptors/token.interceptor';
import { AUTH_SESSION_PORT } from '../application/common/ports/auth-session.port';
import { ANALYSIS_PROVIDER_PORT } from '../application/analysis/application/ports/analysis-provider.port';
import { AnalysisProviderService } from '../application/analysis/provider/services/analysis-provider.service';
import { DASHBOARD_PROVIDER_PORT } from '../application/home/application/ports/dashboard-provider.port';
import { DashboardProviderService } from '../application/home/provider/services/dashboard-provider.service';
import { RECENT_CORRECTIVE_ACTIONS_PROVIDER_PORT } from '../application/home/application/ports/recent-corrective-actions-provider.port';
import { RecentCorrectiveActionsProviderService } from '../application/home/provider/services/recent-corrective-actions-provider.service';
import { NOTIFICATION_PROVIDER_PORT } from '../application/notifications/application/ports/notification-provider.port';
import { NOTIFICATION_REALTIME_PORT } from '../application/notifications/application/ports/notification-realtime.port';
import { NotificationProviderService } from '../application/notifications/provider/services/notification-provider.service';
import { NotificationStompService } from '../application/notifications/provider/services/notification-stomp.service';
import { PROFILE_PROVIDER_PORT } from '../application/profile/application/ports/profile-provider.port';
import { ProfileProviderService } from '../application/profile/provider/services/profile-provider.service';
import { CONFIGURATION_PROVIDER_PORT } from '../application/configuration/application/ports/configuration-provider.port';
import { ConfigurationProviderService } from '../application/configuration/provider/services/configuration-provider.service';
import { API_BASE_URL } from '../application/common/tokens/api-base-url.token';
import { environment } from '../environments/environment';

// PrimeNG
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';

// Animaciones (async OK en Angular 17+)
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from '../application/common/services/transloco-loader.service';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),

    // El orden importa: token primero (añade header), error segundo (captura errores)
    provideHttpClient(withInterceptors([tokenInterceptor, errorInterceptor])),

    // PrimeNG MessageService global para Toast de errores
    MessageService,

    provideTransloco({
      config: {
        availableLangs: ['es', 'en'],
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),

    // URL base del API — inyectable en cualquier provider/service
    {
      provide: API_BASE_URL,
      useValue: environment.apiUrl,
    },

    {
      provide: AUTH_PROVIDER_PORT,
      useExisting: AuthProviderService,
    },

    // Puerto de sesión: guards e interceptors dependen de esta abstracción, no de AuthService
    {
      provide: AUTH_SESSION_PORT,
      useExisting: AuthService,
    },

    {
      provide: ANALYSIS_PROVIDER_PORT,
      useExisting: AnalysisProviderService,
    },

    {
      provide: DASHBOARD_PROVIDER_PORT,
      useExisting: DashboardProviderService,
    },

    {
      provide: RECENT_CORRECTIVE_ACTIONS_PROVIDER_PORT,
      useExisting: RecentCorrectiveActionsProviderService,
    },

    {
      provide: NOTIFICATION_PROVIDER_PORT,
      useExisting: NotificationProviderService,
    },

    {
      provide: NOTIFICATION_REALTIME_PORT,
      useExisting: NotificationStompService,
    },

    {
      provide: PROFILE_PROVIDER_PORT,
      useExisting: ProfileProviderService,
    },

    {
      provide: CONFIGURATION_PROVIDER_PORT,
      useExisting: ConfigurationProviderService,
    },

    // Animaciones (requeridas para overlays)
    provideAnimationsAsync(),

    // PrimeNG Theme + CSS Layer order para convivir con Tailwind
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          // Soporte modo oscuro por data-attr (si lo usas)
          darkModeSelector: '[data-theme="dark"]',
          // Orden de CSS Layers (que coincide con tu orden en styles)
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
      // (Opcional) ajustes globales
      ripple: true,
      zIndex: {
        modal: 1100,
        overlay: 1000,
        menu: 1000,
        tooltip: 1100,
      },
    }),
  ],
};
